// Turn the failure reasons the gateway reports into something a customer can act on.
//
// The gateway sanitizes these server-side, so `reason` is always a stable CODE — never raw error
// text. Internal faults (SMPP bind failures, unhandled exceptions) all arrive as SYSTEM_ERROR.
//
// kind === "dlr"    -> the network gave up after we submitted (LastDlrStatus)
// kind === "status" -> the terminal bucket, when no finer reason was recorded
// kind === "error"  -> the send was refused at submit time

export interface FailureReason {
  reason: string;
  kind: "error" | "dlr" | "status" | string;
  count: number;
}

// Covers both the mapped names the gateway stores (UNDELIVERABLE) and raw SMPP codes (UNDELIV),
// so it stays correct if the stored representation ever changes.
const LABELS: Record<string, string> = {
  // delivery outcomes
  UNDELIVERABLE: "Undeliverable — number invalid or switched off",
  UNDELIV: "Undeliverable — number invalid or switched off",
  EXPIRED: "Expired — handset stayed unreachable",
  REJECTED: "Rejected by the operator",
  REJECTD: "Rejected by the operator",
  DELETED: "Cancelled by the operator",
  FAILED: "Failed before delivery",
  UNKNOWN: "Unknown network error",
  // submit-time causes (sanitized codes)
  INVALID_DESTINATION: "Invalid destination number",
  INVALID_SENDER: "Sender ID rejected",
  THROTTLED: "Throttled by the operator",
  OPTED_OUT: "Recipient opted out",
  BLOCKED_CONTENT: "Blocked — restricted content",
  INSUFFICIENT_CREDIT: "Insufficient credit",
  SYSTEM_ERROR: "Delivery system error"
};

// A short "what do I do about it" line, shown under the biggest cause.
const HINTS: Record<string, string> = {
  UNDELIVERABLE: "Remove these numbers from your list — each attempt still costs credit.",
  UNDELIV: "Remove these numbers from your list — each attempt still costs credit.",
  INVALID_DESTINATION: "Check the number format (country code, no spaces) before resending.",
  EXPIRED: "The phone was off or out of coverage. Usually worth retrying later.",
  REJECTED: "Often a sender-ID or content restriction — check your sender ID is approved.",
  REJECTD: "Often a sender-ID or content restriction — check your sender ID is approved.",
  INVALID_SENDER: "That sender ID isn't accepted by the operator. Request an approved one.",
  THROTTLED: "You sent faster than the operator allows. Spread the campaign out.",
  OPTED_OUT: "These recipients replied STOP. They're on your opt-out list.",
  BLOCKED_CONTENT: "The message matched a restricted-content rule.",
  INSUFFICIENT_CREDIT: "Top up on the Billing page, then resend.",
  SYSTEM_ERROR: "A problem on our side, not yours — our team is notified. Contact support if it persists."
};

export function failureLabel(r: FailureReason): string {
  const key = (r.reason || "").toUpperCase();
  return LABELS[key] ?? r.reason;
}

export function failureHint(r: FailureReason): string | null {
  return HINTS[(r.reason || "").toUpperCase()] ?? null;
}

// Fixed hex (not theme tokens) so the chart segments read in both light and dark.
const STATUS_COLORS: Record<string, string> = {
  FAILED: "#ef4444",
  UNDELIVERABLE: "#f97316",
  EXPIRED: "#f59e0b",
  REJECTED: "#a855f7"
};

export function statusColor(status: string): string {
  return STATUS_COLORS[(status || "").toUpperCase()] ?? "#94a3b8";
}

export function statusLabel(status: string): string {
  const key = (status || "").toUpperCase();
  return LABELS[key] ?? status;
}
